// Helpers
// Get the screen-space rectangle that describes the window that the user can see on the canvas
import { type LngLat, MercatorCoordinate } from "maplibre-gl"
import { vec4 } from "gl-matrix"
import Point from "@mapbox/point-geometry"
import { LngLatBounds } from "maplibre-gl"
import { DEG2RAD, RAD2DEG } from "three/src/math/MathUtils"

export function fitPointToViewportCenter({
    point,
    map,
    zoom,
}: {
    point: { lng: number; lat: number }
    zoom: number
    map: maplibregl.Map
}): LngLat {
    function mercatorXfromLng(lng: number): number {
        return (180 + lng) / 360
    }

    function lngFromMercatorX(x: number): number {
        return x * 360 - 180
    }

    function mercatorYfromLat(lat: number): number {
        return (
            (180 -
                (180 / Math.PI) *
                    Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) /
            360
        )
    }

    function latFromMercatorY(y: number): number {
        const lat =
            ((Math.atan(Math.exp((180 - y * 360) / (180 / Math.PI))) -
                Math.PI / 4) *
                360) /
            Math.PI

        return lat
    }

    function wrap(n: number, min: number, max: number): number {
        const d = max - min
        const w = ((((n - min) % d) + d) % d) + min
        return w === min ? max : w
    }

    const worldSize = map.transform.zoomScale(zoom) * map.transform.tileSize

    const viewport = getViewRect()
    const viewportCenter = new Point(
        (viewport.left + viewport.right) / 2,
        (viewport.top + viewport.bottom) / 2
    )
    // TODO: Account for nudge
    const mapRect = document
        .querySelector("#map canvas")
        ?.getBoundingClientRect()

    if (typeof mapRect === "undefined") {
        throw new Error("Map canvas not found")
    }

    const mapCenter = new Point(
        (mapRect.left + mapRect.right) / 2,
        (mapRect.top + mapRect.bottom) / 2
    )

    const centerDiff = mapCenter.sub(viewportCenter)

    console.log({ centerDiff })

    const pixelsPoint = new Point(
        mercatorXfromLng(point.lng) * worldSize,
        mercatorYfromLat(point.lat) * worldSize
    ).add(centerDiff)

    return {
        lng: lngFromMercatorX(pixelsPoint.x / worldSize),
        lat: latFromMercatorY(pixelsPoint.y / worldSize),
    }
}

// This is aware of edge UI elements that occlude the canvas
export interface Rect {
    left: number
    top: number
    right: number
    bottom: number
    center: { x: number; y: number }
}
export function getViewRect(): Readonly<Rect> {
    // TODO optimization: Implement some kind of cacheing or object reuse, this function is called a lot
    const viewableRect: Partial<Rect> = {}
    let leftPanel = document.querySelector<HTMLElement>("#left-panel")
    // Row mode is where the left bar column moves to the top of the page as a row
    // As seen on mobile devices
    const rowMode =
        leftPanel === null
            ? false
            : getComputedStyle(leftPanel).display === "block"

    let rightPanel: HTMLElement | null = null
    let topPanel: HTMLElement | null = null

    if (rowMode) {
        const searchBar = document.querySelector<HTMLElement>("#search_bar")
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!searchBar?.classList.contains("hide-mobile")) {
            topPanel = searchBar
        } else {
            topPanel = leftPanel
        }
        leftPanel = null
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (topPanel?.classList.contains("offscreen-left")) {
            topPanel = document.querySelector<HTMLElement>(
                ".site-header-menu-bar"
            )
        }
    } else {
        rightPanel = document.querySelector<HTMLElement>("#right-small-panel")
        // topPanel = document.querySelector<HTMLElement>(".site-header-menu-bar")
    }

    const topRect = topPanel?.getBoundingClientRect()
    const leftRect = leftPanel?.getBoundingClientRect()
    const rightRect = rightPanel?.getBoundingClientRect()

    const bottomElements: HTMLElement[] = [
        document.querySelector<HTMLElement>("div.footer"),
        document.querySelector<HTMLElement>(".footer-popup"),
        document.querySelector<HTMLElement>(".navigation_list"),
    ].filter((el) => {
        return el !== null
    }) as HTMLElement[]

    // Find the bottom element with the highest top
    const topEdges = bottomElements.map((element) => {
        if (element.classList.contains("hidden")) return Infinity
        if (element.style.display === "none") return Infinity
        if (element.style.visibility === "hidden") return Infinity
        return element.getBoundingClientRect().top
    })

    viewableRect.bottom = topEdges.reduce(
        (prev, curr) => (curr === 0 ? prev : Math.min(prev, curr)),
        window.innerHeight
    )

    if (typeof leftRect !== "undefined") {
        let zoom = 1
        const style = getComputedStyle(leftPanel as HTMLElement)
        if ("zoom" in style) {
            zoom = parseFloat(style.zoom as string)
        }
        viewableRect.left = leftRect.right * zoom
    } else {
        viewableRect.left = 0
    }

    if (typeof rightRect !== "undefined") {
        let zoom = 1
        {
            const style = getComputedStyle(leftPanel as HTMLElement)
            if ("zoom" in style) {
                zoom *= parseFloat(style.zoom as string)
            }
        }
        {
            const style = getComputedStyle(rightPanel as HTMLElement)
            if ("zoom" in style) {
                zoom *= parseFloat(style.zoom as string)
            }
        }
        viewableRect.right = window.innerWidth - rightRect.width * zoom
    } else {
        viewableRect.right = window.innerWidth
    }

    if (typeof topRect !== "undefined") {
        let zoom = 1
        const style = getComputedStyle(topPanel as HTMLElement)
        if ("zoom" in style) {
            zoom *= parseFloat(style.zoom as string)
        }
        viewableRect.top = topRect.bottom * zoom
    } else {
        viewableRect.top = 0
    }

    viewableRect.center = {
        x: (viewableRect.left + viewableRect.right) / 2,
        y: (viewableRect.top + viewableRect.bottom) / 2,
    }
    return viewableRect as Readonly<Rect>
}

/**
 * Fit the element to the given rectangle
 * @param el Element to fit
 * @param rect Rectangle to fit el in
 */
export function fit(el: HTMLElement, rect: Rect): void {
    el.style.position = "absolute"
    el.style.left = `${rect.left}px`
    el.style.top = `${rect.top}px`
    el.style.width = `${rect.right - rect.left}px`
    el.style.height = `${rect.bottom - rect.top}px`
}

export function debugDrawRect(rect): void {
    let el = document.querySelector<HTMLElement>(".debug_rect")
    if (el === null) {
        el = document.createElement("div")
        document.body.append(el)
    }
    el.classList.add("debug_rect")
    el.style.backgroundColor = "rgba(255, 255, 0, 0.2)"
    el.style.zIndex = "1000000000000000"
    el.style.pointerEvents = "none"
    fit(el, rect)
}

// export function setMaxBoundsContain()

/**
 * Transform a boundary from map space into view space
 * @param bounds Boundary in map space
 * @returns Boundary that will bound view space as if it were map space
 */
export function mapBoundsToViewBounds(
    map: maplibregl.Map,
    bounds: LngLatBounds
): LngLatBounds {
    const view = getViewRect()
    const mapRect = map.getCanvas().getBoundingClientRect()

    // Transform the longitude
    // TODO: Make this work with Alaska/wrapped bounds
    /**
     * Width of the screen
     */
    const width = view.right - view.left
    /**
     * Total longitude subtended by bounds
     */
    const lngSpan = bounds.getEast() - bounds.getWest()
    /**
     * Degrees of longitude per view pixel
     */
    const lngPerViewPixel = lngSpan / width
    /**
     * Amount of pixels the map is offset to the left (relative to the view rect)
     */
    const leftPixelOffset = mapRect.left - view.left
    /**
     * Amount of longitude the map is offset to the west (relative to the view rect)
     */
    const westLngOffset = lngPerViewPixel * leftPixelOffset
    const w = bounds.getWest() + westLngOffset

    // Same process for east side
    const rightPixelOffset = mapRect.right - view.right
    const eastLngOffset = lngPerViewPixel * rightPixelOffset
    const e = bounds.getEast() + eastLngOffset

    // Transform the latitude
    const isoN = isometricLatitude(bounds.getNorth())
    const isoS = isometricLatitude(bounds.getSouth())

    // Same process with latitude, with the extra step of converting to and from isometric latitude
    const height = view.bottom - view.top
    const latSpan = isoN - isoS
    const latPerViewPixel = latSpan / height
    const topPixelOffset = mapRect.top - view.top
    const northLatOffset = latPerViewPixel * topPixelOffset
    const n = latitudeFromIsometricLat(isoN + northLatOffset)

    const bottomPixelOffset = mapRect.bottom - view.bottom
    const southLatOffset = latPerViewPixel * bottomPixelOffset
    const s = latitudeFromIsometricLat(isoS - southLatOffset)

    // debugger
    return new LngLatBounds([w, s, e, n])
}

/** Convert from latitude to iso~metric latitude
 *
 * Every unit of isometric latitude spans the same "mercator map distance" as one unit of longitude
 * @param lat latitude in degrees
 * @returns isometric latitude in degrees
 */
function isometricLatitude(lat: number): number {
    return RAD2DEG * Math.log(Math.tan(Math.PI / 4 + (DEG2RAD * lat) / 2))
}

/**
 * The inverse of isometricLatitude()
 * @param isoLat isometric latutide in degrees
 * @returns latitude in degrees
 */
function latitudeFromIsometricLat(isoLat: number): number {
    // isoLat = RAD2DEG * Math.log(Math.tan(Math.PI / 4 + (DEG2RAD * lat) / 2))
    // isoLat * DEG2RAD = Math.log(Math.tan(Math.PI / 4 + (DEG2RAD * lat) / 2))
    // Math.exp(isoLat * DEG2RAD) = Math.tan(Math.PI / 4 + (DEG2RAD * lat) / 2)
    const lat =
        RAD2DEG * (Math.PI / 2 - 2 * Math.atan(Math.exp(isoLat * DEG2RAD)))
    if (isoLat >= 0) return lat * -1
    if (isoLat < 0) return lat
    return 0
}
